import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { JournalEntryTransaction } from "components/JournalEntry/AddJournalEntryModal";
import { AddJournalEntryTable } from "components/JournalEntry/AddJournalEntryTable";
import { JournalEntryFormWrapper } from "components/JournalEntry/JournalEntryFormWrapper";
import { JournalEntryInfo } from "components/JournalEntry/JournalEntryInfo";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ACCOUNTING_METHODS } from "constants/accountMethod";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { LT_SERIAL_NUMBER_PREFIX } from "constants/journalEntrySources";
import dayjs from "dayjs";
import { journalEntrySchema } from "formValidations/JournalEntrySchema";
import { FormikValues } from "formik";
import { useToast } from "hooks/useToast";
import randomBytes from "randombytes";
import { useAddPayrollMutation } from "store/apis/payroll";
import { LineItem, PayrollImport, Vendor } from "types/Models/payroll";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { getMaxExistingSerialNumber } from "utils/journalEntryUtils";

type PayrollJEProps = {
  jeData?: PayrollImport;
  entityId: string;
} & ModalProps;

type PayrollFormValues = {
  transactions: Omit<JournalEntryTransaction, "id">[];
  transaction_date: string;
  ledger_entry_name: string;
  ledger_type: string;
  other_ledger_type_str: string;
  is_recurring_enabled: boolean;
  frequency: string;
  end_date: string;
  max_recurring_count: number;
  ends_on: string;
  payroll_period_start_date: string;
  payroll_period_end_date: string;
  pay_day_date: string;
  gross_pay: number;
  total_employee_taxes: number;
  total_employer_taxes: number;
  reimbursement: number;
  deductions: number;
  file_id: string;
};

const transformLineItems = ({
  items,
  vendor,
}: {
  items: LineItem[];
  vendor: Vendor;
}) => {
  return items.map((item, index) => ({
    merchant: vendor.uuid,
    accrual_category_id: item.category?.uuid || "",
    cash_category_id: undefined,
    description: item.description!,
    credit: item.type === "credit" ? item.amount : 0,
    debit: item.type === "debit" ? item.amount : 0,
    invoice: null,
    uuid: randomBytes(10).toString("hex"),
    id: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
    entry_type: ACCOUNTING_METHODS.ACCRUAL,
    ledger_entry_serial_number: `${LT_SERIAL_NUMBER_PREFIX}${(
      index + 101
    ).toString()}`,
  }));
};

const createInitialValues = ({
  jeData,
  initialTransactions,
}: {
  jeData?: PayrollImport;
  initialTransactions: JournalEntryTransaction[];
}) => {
  return {
    transactions: initialTransactions,
    transaction_date: dayjs(jeData?.transaction_date).format(DISPLAY_DATE),
    ledger_entry_name: jeData?.ledger_entry_name || "",
    ledger_type: jeData?.ledger_type || "PAYROLL",
    other_ledger_type_str: "",
    is_recurring_enabled: false,
    frequency: "MONTHLY",
    end_date: "",
    max_recurring_count: 0,
    ends_on: "NEVER_ENDS",
    payroll_period_start_date: jeData?.payroll_period_start_date || "",
    payroll_period_end_date: jeData?.payroll_period_end_date || "",
    pay_day_date: jeData?.pay_day_date || "",
    gross_pay: jeData?.gross_pay || 0,
    total_employee_taxes: jeData?.total_employee_taxes || 0,
    total_employer_taxes: jeData?.total_employer_taxes || 0,
    reimbursement: jeData?.reimbursement || 0,
    deductions: jeData?.deductions || 0,
    file_id: jeData?.file_id || "",
    basis: ACCOUNTING_METHODS.ACCRUAL,
  };
};

const transformTransactionList = (
  transactions: Omit<JournalEntryTransaction, "id">[]
) => {
  return transactions.map(
    ({
      credit,
      debit,
      cash_category_id,
      accrual_category_id,
      description,
      invoice,
      merchant,
    }) => ({
      amount: Math.abs(Number(credit)) || Math.abs(Number(debit)) * -1,
      description: description!,
      invoice_id: invoice?.uuid || null,
      merchant_data_id: merchant,
      accrual_category_id: accrual_category_id || cash_category_id || null,
    })
  );
};

const validatePayrollData = (values: PayrollFormValues): boolean => {
  const requiredFields = [
    values.payroll_period_start_date,
    values.payroll_period_end_date,
    values.pay_day_date,
    values.gross_pay,
    values.total_employee_taxes,
    values.total_employer_taxes,
    values.reimbursement,
    values.deductions,
    values.file_id,
  ];

  return requiredFields.every((field) => field !== undefined && field !== null);
};

const createPayrollPayload = (values: PayrollFormValues) => {
  const transactionList = transformTransactionList(values.transactions);

  return {
    transactions: transactionList,
    transaction_date: dayjs(values.transaction_date).format(YYYY_MM_DD),
    ledger_entry_name: values.ledger_entry_name,
    ledger_type: values.ledger_type,
    other_ledger_type_str: values.other_ledger_type_str || undefined,
    is_recurring_enabled: values.is_recurring_enabled,
    max_recurring_count: values.end_date
      ? undefined
      : Number(values.max_recurring_count),
    frequency: values.frequency,
    end_date:
      values.end_date && !values.max_recurring_count
        ? dayjs(values.end_date).format(YYYY_MM_DD)
        : undefined,
    payroll_period_start_date: values.payroll_period_start_date,
    payroll_period_end_date: values.payroll_period_end_date,
    pay_day_date: values.pay_day_date,
    gross_pay: values.gross_pay,
    total_employee_taxes: values.total_employee_taxes,
    total_employer_taxes: values.total_employer_taxes,
    reimbursement: values.reimbursement,
    deductions: values.deductions,
    file_id: values.file_id,
  };
};

export default function PayrollJE({
  isOpen,
  close,
  jeData,
  entityId,
}: PayrollJEProps) {
  const initialTransactions =
    jeData?.line_items && jeData?.vendor
      ? transformLineItems({ items: jeData.line_items, vendor: jeData.vendor })
      : [];
  const initialValues = createInitialValues({ jeData, initialTransactions });
  const { successToast, alertToast } = useToast();
  const [addPayroll, { isLoading }] = useAddPayrollMutation();

  const onSubmit = async (formikValues: FormikValues) => {
    const values = formikValues as PayrollFormValues;

    if (!validatePayrollData(values)) {
      alertToast({ message: "Please fill in all required payroll fields" });
      return;
    }

    try {
      const payload = createPayrollPayload(values);
      await addPayroll({
        entityId,
        payload,
      }).unwrap();
      successToast({ title: "Report imported successfully" });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild size="xxxl" useCustomOverlay>
        <FormikForm
          initialValues={initialValues}
          onSubmit={onSubmit}
          validateOnChange
          validationSchema={journalEntrySchema}
        >
          {({ values, submitForm }) => (
            <>
              <Modal.Header>
                <div>
                  <Modal.Title>Journal Entry</Modal.Title>
                  <Modal.Subtitle>
                    Generated from your Gusto payroll report. Review the entries
                    below and approve to add to your books.
                  </Modal.Subtitle>
                </div>
                <Modal.Close />
              </Modal.Header>

              <JournalEntryFormWrapper>
                {({ isAmountMatch, isValidTransactions }) => (
                  <>
                    <Modal.Body>
                      <div className="t-flex t-flex-col t-gap-4">
                        <JournalEntryInfo />
                        <AddJournalEntryTable
                          allEntries={values.transactions}
                          maxSerialNumber={getMaxExistingSerialNumber([])}
                        />
                      </div>
                    </Modal.Body>

                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button type="reset" disabled={isLoading}>
                          Cancel
                        </Button>
                      </Modal.RawClose>

                      <ConditionalToolTip
                        condition={
                          !isAmountMatch && (
                            <span>
                              Total debits and credits
                              <br /> should match
                            </span>
                          )
                        }
                      >
                        <span>
                          <Button
                            customType="primary"
                            type="submit"
                            onClick={submitForm}
                            disabled={!isValidTransactions || isLoading}
                            isLoading={isLoading}
                          >
                            Approve
                          </Button>
                        </span>
                      </ConditionalToolTip>
                    </Modal.FooterButtonGroup>
                  </>
                )}
              </JournalEntryFormWrapper>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
}
